import * as React from 'react';
import type { Meta, StoryObj } from '@storybook/react';
import { Table } from '../Table';
import type { SortKey } from '../Table/types';

const meta: Meta<typeof Table> = {
  title: 'Components/Table',
  component: Table,
};

export default meta;
type Story = StoryObj<typeof Table>;

function TableStory({ columns, ...props }: React.ComponentProps<typeof Table>) {
  return <Table columns={columns} {...props} />;
}

function TableSortingStory({ columns, data, ...props }: React.ComponentProps<typeof Table>) {
  const [sortKey, setSortKey] = React.useState<SortKey>();
  const sortedData = React.useMemo(() => {
    if (!sortKey) return data;
    return data.slice().sort((a, b) => (sortKey.desc ? -1 : 1) * a[sortKey.key].localeCompare(b[sortKey.key]));
  }, [data, sortKey]);

  return (
    <Table
      {...props}
      columns={columns}
      data={sortedData}
      sortedBy={sortKey}
      onSortingRequested={sortKey => {
        console.log(sortKey);
        setSortKey(sortKey);
      }}
    />
  );
}

export const Default: Story = {
  render: TableStory,
  args: {
    data: [
      { title: 'The Shawshank Redemption', releaseYear: '1994', rating: 9.3 },
      { title: 'The Godfather', releaseYear: '1972', rating: 9.2 },
      { title: 'The Godfather: Part II', releaseYear: '1974', rating: 9.0 },
      { title: 'The Dark Knight', releaseYear: '2008', rating: 9.0 },
      { title: '12 Angry Men', releaseYear: '1957', rating: 8.9 },
      { title: "Schindler's List", releaseYear: '1993', rating: 8.9 },
      { title: 'The Lord of the Rings: The Return of the King', releaseYear: '2003', rating: 8.9 },
      { title: 'Pulp Fiction', releaseYear: '1994', rating: 8.9 },
      { title: 'The Lord of the Rings: The Fellowship of the Ring', releaseYear: '2001', rating: 8.8 },
      { title: 'Forrest Gump', releaseYear: '1994', rating: 8.8 },
    ],
    columns: [
      { Header: 'Title', accessor: 'title' },
      { Header: 'Release Year', accessor: 'releaseYear' },
      { Header: 'Rating', accessor: 'rating' },
    ],
  },
};

const labels = ['Interscope', 'Universal'];

export const PaginationAndSorting: Story = {
  name: 'Pagination and Sorting',
  render: TableSortingStory,
  args: {
    pageSize: 25,
    paginationPosition: 'bottomRight',
    data: Array(1000)
      .fill(0)
      .map((_, i, arr) => ({
        title: `Album #${i + 1}`,
        released: `July 8, ${1997 + i}`,
        label: labels[i % labels.length],
        chart: arr.length - i,
        id: crypto.randomUUID(),
      })),
    columns: [
      {
        Header: 'Title',
        accessor: 'title',
        sortable: true,
      },
      {
        Header: 'Released',
        accessor: 'released',
        sortable: true,
      },
      {
        Header: 'Label',
        accessor: 'label',
      },
      {
        Header: 'Chart Position',
        accessor: 'chart',
      },
    ],
  },
};

export const LoadingWithData: Story = {
  render: TableStory,
  args: {
    data: [
      { title: 'The Shawshank Redemption', releaseYear: '1994', rating: 9.3 },
      { title: 'The Godfather', releaseYear: '1972', rating: 9.2 },
      { title: 'The Godfather: Part II', releaseYear: '1974', rating: 9.0 },
      { title: 'The Dark Knight', releaseYear: '2008', rating: 9.0 },
      { title: '12 Angry Men', releaseYear: '1957', rating: 8.9 },
      { title: "Schindler's List", releaseYear: '1993', rating: 8.9 },
      { title: 'The Lord of the Rings: The Return of the King', releaseYear: '2003', rating: 8.9 },
      { title: 'Pulp Fiction', releaseYear: '1994', rating: 8.9 },
      { title: 'The Lord of the Rings: The Fellowship of the Ring', releaseYear: '2001', rating: 8.8 },
      { title: 'Forrest Gump', releaseYear: '1994', rating: 8.8 },
    ],
    columns: [
      { Header: 'Title', accessor: 'title' },
      { Header: 'Release Year', accessor: 'releaseYear' },
      { Header: 'Rating', accessor: 'rating' },
    ],
    loading: true,
  },
};

export const LoadingNoData: Story = {
  render: TableStory,
  args: {
    data: [],
    columns: [
      { Header: 'Title', accessor: 'title' },
      { Header: 'Release Year', accessor: 'releaseYear' },
      { Header: 'Rating', accessor: 'rating' },
    ],
    loading: true,
  },
};

export const CustomCellRender: Story = {
  render: TableStory,
  args: {
    data: [
      { title: 'The Shawshank Redemption', releaseYear: '1994', rating: 9.3 },
      { title: 'The Godfather', releaseYear: '1972', rating: 9.2 },
      { title: 'The Godfather: Part II', releaseYear: '1974', rating: 9.0 },
      { title: 'The Dark Knight', releaseYear: '2008', rating: 9.0 },
      { title: '12 Angry Men', releaseYear: '1957', rating: 8.9 },
      { title: "Schindler's List", releaseYear: '1993', rating: 8.9 },
      { title: 'The Lord of the Rings: The Return of the King', releaseYear: '2003', rating: 8.9 },
      { title: 'Pulp Fiction', releaseYear: '1994', rating: 8.9 },
      { title: 'The Lord of the Rings: The Fellowship of the Ring', releaseYear: '2001', rating: 8.8 },
      { title: 'Forrest Gump', releaseYear: '1994', rating: 8.8 },
    ],
    columns: [
      { Header: 'Title', accessor: 'title' },
      { Header: 'Release Year', accessor: 'releaseYear' },
      {
        Header: 'Rating',
        accessor: 'rating',
        Cell(row) {
          return <span>⭐️ {row.original.rating}</span>;
        },
      },
    ],
  },
};
